/**
 * Create or edit one notice.
 *
 * TWO THINGS THIS SCREEN IS CAREFUL ABOUT
 * ---------------------------------------
 * 1. **Ivanti owns Ivanti's words.** A mirrored alert's title, kind and body
 *    belong to the tenant; PATCHing them is a 409 by contract. Rather than let
 *    an admin type a new title and discover that on save, the content fields are
 *    disabled and the reason is stated at the top of the form. The patch we send
 *    for an `IVANTI` row carries placement and rank and nothing else, so the
 *    409 is unreachable from this UI rather than merely unlikely.
 *
 * 2. **The body is Markdown and is previewed live.** No WYSIWYG: the stored
 *    value has to stay Markdown (it is rendered by a renderer that emits React
 *    elements and cannot be talked into emitting HTML), and a rich-text editor
 *    whose output is round-tripped through Markdown is a reliable way to lose
 *    an admin's formatting. A textarea beside the real renderer shows exactly
 *    what an employee will see, because it *is* what an employee will see.
 */

import { Alert, Button, Dialog, Field, Input, NumberField, Select, Textarea } from "@norican/react";
import { type FormEvent, useState } from "react";

import { ErrorState, Markdown } from "@/components/common";
import { useT } from "@/i18n";
import type { Message, MessageKind, MessagePlacement } from "@/lib/api";

import { useCreateMessage, useUpdateMessage } from "./api";
import {
  isReadOnlySource,
  MESSAGE_KIND_ORDER,
  MESSAGE_PLACEMENTS,
  useMessageKindLabel,
  useMessagePlacementLabel,
} from "./message-kinds";

/** Where a new notice starts: visible on the board, not yet on the home page. */
const DEFAULT_KIND: MessageKind = "NEWS";
const DEFAULT_PLACEMENT: MessagePlacement = "CENTER";

export interface MessageFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The notice being edited, or `null` to create a new local one. */
  message: Message | null;
}

interface MessageFormProps {
  message: Message | null;
  onDone: () => void;
}

function MessageForm({ message, onDone }: MessageFormProps) {
  const { t } = useT("messages");
  const kindLabel = useMessageKindLabel();
  const placementLabel = useMessagePlacementLabel();

  const isReadOnly = message !== null && isReadOnlySource(message.source);

  const [kind, setKind] = useState<MessageKind>(message?.kind ?? DEFAULT_KIND);
  const [title, setTitle] = useState(message?.title ?? "");
  const [body, setBody] = useState(message?.body_markdown ?? "");
  const [placement, setPlacement] = useState<MessagePlacement>(
    message?.placement ?? DEFAULT_PLACEMENT,
  );
  const [rank, setRank] = useState<number>(message?.rank ?? 0);

  const create = useCreateMessage();
  const update = useUpdateMessage();
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const titleMissing = title.trim() === "";

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (titleMissing || pending) {
      return;
    }

    if (message === null) {
      create.mutate(
        { kind, title: title.trim(), body_markdown: body, placement, rank },
        { onSuccess: onDone },
      );
      return;
    }

    update.mutate(
      {
        messageId: message.message_id,
        // An Ivanti row gets placement and rank only — the fields we own.
        patch: isReadOnly
          ? { placement, rank }
          : { kind, title: title.trim(), body_markdown: body, placement, rank },
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="contents">
      <Dialog.Body className="flex flex-col gap-4 overflow-y-auto">
        {isReadOnly ? (
          <Alert
            tone="info"
            title={
              message.source === "IVANTI"
                ? t("form.readOnlyTitle.IVANTI")
                : t("form.readOnlyTitle.BUILTIN")
            }
          >
            {message.source === "IVANTI"
              ? t("form.readOnlyBody.IVANTI")
              : t("form.readOnlyBody.BUILTIN")}
          </Alert>
        ) : null}

        {error == null ? null : <ErrorState error={error} variant="inline" />}

        <Field label={t("form.kind")} disabled={isReadOnly}>
          <Select.Root
            value={kind}
            onValueChange={(next) => setKind(next as MessageKind)}
            disabled={isReadOnly}
          >
            <Select.Trigger aria-label={t("form.kind")}>
              <Select.Value />
            </Select.Trigger>
            <Select.Portal>
              <Select.Positioner sideOffset={4}>
                <Select.Popup>
                  <Select.List>
                    {MESSAGE_KIND_ORDER.map((option) => (
                      <Select.Item key={option} value={option}>
                        <Select.ItemText>{kindLabel(option)}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Popup>
              </Select.Positioner>
            </Select.Portal>
          </Select.Root>
        </Field>

        <Field
          label={t("form.title")}
          required={!isReadOnly}
          disabled={isReadOnly}
          {...(isReadOnly ? { hint: t("form.readOnlyHint") } : {})}
        >
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={isReadOnly}
            maxLength={200}
          />
        </Field>

        <Field label={t("form.body")} disabled={isReadOnly} hint={t("form.bodyHint")}>
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            disabled={isReadOnly}
            rows={8}
            className="font-mono"
          />
        </Field>

        <div className="border-border-subtle bg-bg-subtle flex flex-col gap-2 rounded-md border p-3">
          <span className="text-fg-muted text-xs tracking-wide uppercase">{t("form.preview")}</span>
          {body.trim() === "" ? (
            <span className="text-fg-subtle text-sm">{t("form.previewEmpty")}</span>
          ) : (
            <Markdown>{body}</Markdown>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("form.placement")} hint={t("form.placementHint")}>
            <Select.Root
              value={placement}
              onValueChange={(next) => setPlacement(next as MessagePlacement)}
            >
              <Select.Trigger aria-label={t("form.placement")}>
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner sideOffset={4}>
                  <Select.Popup>
                    <Select.List>
                      {MESSAGE_PLACEMENTS.map((option) => (
                        <Select.Item key={option} value={option}>
                          <Select.ItemText>{placementLabel(option)}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </Field>

          <Field label={t("form.rank")} hint={t("form.rankHint")}>
            <NumberField.Root
              value={rank}
              onValueChange={(next) => setRank(next ?? 0)}
              min={0}
              max={999}
              step={1}
            >
              <NumberField.Group>
                <NumberField.Decrement aria-label={t("form.rankDown")} />
                <NumberField.Input aria-label={t("form.rank")} />
                <NumberField.Increment aria-label={t("form.rankUp")} />
              </NumberField.Group>
            </NumberField.Root>
          </Field>
        </div>
      </Dialog.Body>

      <Dialog.Footer>
        <Dialog.Close render={<Button variant="outline" tone="neutral" type="button" />}>
          {t("form.cancel")}
        </Dialog.Close>
        <Button type="submit" loading={pending} disabled={titleMissing}>
          {message === null ? t("form.create") : t("form.save")}
        </Button>
      </Dialog.Footer>
    </form>
  );
}

export function MessageFormDialog({ open, onOpenChange, message }: MessageFormDialogProps) {
  const { t } = useT("messages");

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="lg" className="flex max-h-[85vh] w-full flex-col">
          <Dialog.Header>
            <Dialog.Title>
              {message === null ? t("form.createTitle") : t("form.editTitle")}
            </Dialog.Title>
            <Dialog.CloseButton label={t("form.cancel")} />
          </Dialog.Header>
          {/* Keyed so switching which row is being edited remounts the form and
              re-seeds every field from that row, without an effect syncing
              props into state. */}
          <MessageForm
            key={message?.message_id ?? "new"}
            message={message}
            onDone={() => onOpenChange(false)}
          />
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
